import { createApi } from '@reduxjs/toolkit/query/react';
import { BackendResponse } from '../../types';
import { parseQueryParams } from '../../utils';
import { baseQueryWithReauth } from './baseQuery';
import { SalesOrderTableConfigPage } from './salesOrderTableConfig';

/** Page key used by line-item table-config apply + active APIs */
export const LINE_ITEM_REPORT_TABLE_CONFIG_PAGE =
  SalesOrderTableConfigPage.LINE_ITEM_REPORTS;

export interface SalesOrderLineItemColumnConfigItem {
  key: string;
  label: string;
  visible: boolean;
  order: number;
}

export interface CreateSalesOrderLineItemTableConfigDto {
  name: string;
  fieldConfigJson: {
    data: SalesOrderLineItemColumnConfigItem[];
  };
  isDefault?: boolean;
}

export interface UpdateSalesOrderLineItemTableConfigDto {
  name?: string;
  fieldConfigJson?: {
    data: SalesOrderLineItemColumnConfigItem[];
  };
  isDefault?: boolean;
}

export interface ApplySalesOrderLineItemTableConfigDto {
  configId: number;
  page: string;
}

export interface SalesOrderLineItemTableConfig {
  id: number;
  name: string;
  fieldConfigJson?: {
    data: SalesOrderLineItemColumnConfigItem[];
  };
  data?: SalesOrderLineItemColumnConfigItem[];
  isDefault?: boolean;
  createdAt?: string;
  updatedAt?: string;
  createdByUser?: {
    ID: number;
    FullName: string;
    EmailID: string;
  };
}

function parseLineItemFieldConfigJson(
  fieldConfigJson: unknown,
): { data: SalesOrderLineItemColumnConfigItem[] } | undefined {
  if (fieldConfigJson == null) return undefined;

  let parsed: unknown = fieldConfigJson;
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch {
      return undefined;
    }
  }

  if (Array.isArray(parsed)) {
    return { data: parsed as SalesOrderLineItemColumnConfigItem[] };
  }

  if (
    parsed &&
    typeof parsed === 'object' &&
    Array.isArray((parsed as { data?: unknown }).data)
  ) {
    return parsed as { data: SalesOrderLineItemColumnConfigItem[] };
  }

  return undefined;
}

export function getSalesOrderLineItemConfigColumns(
  config?: SalesOrderLineItemTableConfig | null,
): SalesOrderLineItemColumnConfigItem[] {
  if (!config) return [];

  const fieldConfig =
    parseLineItemFieldConfigJson(config.fieldConfigJson) ||
    (Array.isArray(config.data) ? { data: config.data } : undefined);

  return [...(fieldConfig?.data ?? [])]
    .filter(column => column.visible !== false)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
}

/** Unwrap nested BackendResponse / mapping payloads from RTK query `data`. */
export function unwrapSalesOrderLineItemApiPayload(
  response: unknown,
): unknown {
  if (response == null) return null;

  let current: unknown = response;

  for (let depth = 0; depth < 5; depth++) {
    if (current == null || typeof current !== 'object') break;

    const rec = current as Record<string, unknown>;

    if (rec.config && typeof rec.config === 'object') {
      return rec.config;
    }

    if (
      rec.fieldConfigJson != null ||
      rec.configId != null ||
      rec.tableConfigId != null ||
      rec.salesOrderLineItemTableConfigId != null ||
      (typeof rec.id === 'number' && (rec.name != null || rec.fieldConfigJson != null))
    ) {
      return current;
    }

    if ('data' in rec && rec.data != null && rec.data !== current) {
      current = rec.data;
      continue;
    }

    break;
  }

  return current;
}

/** Config id from active/apply API when full fieldConfigJson is not included. */
export function extractSalesOrderLineItemActiveConfigId(
  raw: unknown,
): number | undefined {
  const unwrapped = unwrapSalesOrderLineItemApiPayload(raw);
  if (!unwrapped || typeof unwrapped !== 'object') return undefined;

  const record = unwrapped as Record<string, unknown>;
  if (typeof record.configId === 'number') return record.configId;
  if (typeof record.tableConfigId === 'number') return record.tableConfigId;
  if (typeof record.salesOrderLineItemTableConfigId === 'number') {
    return record.salesOrderLineItemTableConfigId;
  }

  if (record.config && typeof record.config === 'object') {
    const config = record.config as Record<string, unknown>;
    if (typeof config.id === 'number') return config.id;
  }

  if (typeof record.id === 'number') return record.id;
  return undefined;
}

export function normalizeSalesOrderLineItemTableConfig(
  raw: unknown,
): SalesOrderLineItemTableConfig | null {
  if (!raw || typeof raw !== 'object') return null;

  const record = raw as Record<string, unknown>;
  const nested =
    record.config && typeof record.config === 'object'
      ? (record.config as SalesOrderLineItemTableConfig)
      : (raw as SalesOrderLineItemTableConfig);

  const fieldConfigJson =
    parseLineItemFieldConfigJson(nested.fieldConfigJson) ||
    (Array.isArray(nested.data) ? { data: nested.data } : undefined);

  if (!fieldConfigJson?.data?.length) {
    const id = extractSalesOrderLineItemActiveConfigId(raw);
    if (id) {
      return { ...nested, id };
    }
    return null;
  }

  return {
    ...nested,
    fieldConfigJson,
  };
}

/** Load full config for reports table (active API + optional persisted id). */
export async function resolveSalesOrderLineItemTableConfig(
  response: unknown,
  fetchById: (id: number) => Promise<unknown>,
  persistedConfigId?: number | null,
): Promise<SalesOrderLineItemTableConfig | null> {
  const raw = unwrapSalesOrderLineItemApiPayload(response);
  let normalized = normalizeSalesOrderLineItemTableConfig(raw);

  const configId =
    extractSalesOrderLineItemActiveConfigId(raw) ??
    normalized?.id ??
    persistedConfigId ??
    undefined;

  if (configId) {
    try {
      const fullResponse = await fetchById(configId);
      const fullNormalized = normalizeSalesOrderLineItemTableConfig(
        unwrapSalesOrderLineItemApiPayload(fullResponse),
      );
      if (
        fullNormalized &&
        getSalesOrderLineItemConfigColumns(fullNormalized).length > 0
      ) {
        return fullNormalized;
      }
    } catch {
      /* fall through to normalized */
    }
  }

  if (normalized && getSalesOrderLineItemConfigColumns(normalized).length > 0) {
    return normalized;
  }

  return null;
}

const salesOrderLineItemTableConfigApi = createApi({
  reducerPath: 'salesOrderLineItemTableConfig',
  baseQuery: baseQueryWithReauth,
  tagTypes: ['SalesOrderLineItemTableConfig'],
  endpoints: builder => ({
    getSalesOrderLineItemTableConfigList: builder.query<
      any,
      void
    >({
      query: () => ({
        url: '/sales-order-line-item-table-config',
        method: 'GET',
      }),
      providesTags: ['SalesOrderLineItemTableConfig'],
    }),

    getActiveSalesOrderLineItemTableConfig: builder.query<any, void>({
      async queryFn(_arg, _api, _extraOptions, fetchWithBQ) {
        const pages = [
          LINE_ITEM_REPORT_TABLE_CONFIG_PAGE,
          SalesOrderTableConfigPage.REPORTS,
        ];

        let lastResult: Awaited<ReturnType<typeof fetchWithBQ>> | undefined;

        for (const page of pages) {
          const result = await fetchWithBQ({
            url: `/sales-order-line-item-table-config/active${parseQueryParams({
              page,
            })}`,
            method: 'GET',
          });
          lastResult = result;

          if (result.error) continue;

          const raw = unwrapSalesOrderLineItemApiPayload(result.data);
          const configId = extractSalesOrderLineItemActiveConfigId(raw);
          const normalized = normalizeSalesOrderLineItemTableConfig(raw);

          if (
            configId ||
            (normalized && getSalesOrderLineItemConfigColumns(normalized).length > 0)
          ) {
            return { data: result.data };
          }
        }

        if (lastResult?.error) {
          return { error: lastResult.error as any };
        }

        return { data: lastResult?.data };
      },
      providesTags: ['SalesOrderLineItemTableConfig'],
    }),

    getSalesOrderLineItemTableConfig: builder.query<
      BackendResponse<SalesOrderLineItemTableConfig>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/sales-order-line-item-table-config/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, { id }) => [
        { type: 'SalesOrderLineItemTableConfig', id },
      ],
    }),

    createSalesOrderLineItemTableConfig: builder.mutation<
      BackendResponse<SalesOrderLineItemTableConfig>,
      CreateSalesOrderLineItemTableConfigDto
    >({
      query: payload => ({
        url: '/sales-order-line-item-table-config',
        method: 'POST',
        body: payload,
      }),
    }),

    updateSalesOrderLineItemTableConfig: builder.mutation<
      BackendResponse<SalesOrderLineItemTableConfig>,
      { id: number; data: UpdateSalesOrderLineItemTableConfigDto }
    >({
      query: ({ id, data }) => ({
        url: `/sales-order-line-item-table-config/${id}`,
        method: 'PUT',
        body: data,
      }),
    }),

    deleteSalesOrderLineItemTableConfig: builder.mutation<
      BackendResponse<void>,
      { id: number }
    >({
      query: ({ id }) => ({
        url: `/sales-order-line-item-table-config/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['SalesOrderLineItemTableConfig'],
    }),

    applySalesOrderLineItemTableConfig: builder.mutation<
      BackendResponse<void>,
      ApplySalesOrderLineItemTableConfigDto
    >({
      query: payload => ({
        url: '/sales-order-line-item-table-config/apply',
        method: 'POST',
        body: {
          configId: payload.configId,
          page: payload.page || LINE_ITEM_REPORT_TABLE_CONFIG_PAGE,
        },
      }),
      invalidatesTags: ['SalesOrderLineItemTableConfig'],
    }),
  }),
});

export const {
  useGetSalesOrderLineItemTableConfigListQuery,
  useGetActiveSalesOrderLineItemTableConfigQuery,
  useGetSalesOrderLineItemTableConfigQuery,
  useLazyGetSalesOrderLineItemTableConfigQuery,
  useCreateSalesOrderLineItemTableConfigMutation,
  useUpdateSalesOrderLineItemTableConfigMutation,
  useDeleteSalesOrderLineItemTableConfigMutation,
  useApplySalesOrderLineItemTableConfigMutation,
} = salesOrderLineItemTableConfigApi;

export default salesOrderLineItemTableConfigApi;
